import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowRight, CheckCircle, Factory, Shield, Truck, Users, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { products } from '../mock';

export const Home = () => {
  const featuredProducts = products.slice(2, 5);
  const { t } = useTranslation();

  // Get translated product data
  const getProductData = (product) => {
    const itemData = t(`products.items.${product.id}`, { returnObjects: true });
    return {
      ...product,
      name: itemData?.name || product.id,
      shortDesc: itemData?.shortDesc || '',
      material: itemData?.material || '',
      compliance: itemData?.compliance || ''
    };
  };

  const translatedFeaturedProducts = featuredProducts.map(getProductData);

  const getTypeTranslation = (type) => {
    const typeMap = {
      'residential': t('products.residential'),
      'municipal': t('products.municipal'),
      'industrial': t('products.industrial')
    };
    return typeMap[type] || type;
  };

  return (
    <div className="flex flex-col">
      {/* Hero Section */}
      <section className="relative min-h-[500px] sm:min-h-[600px] lg:min-h-[700px] flex items-center justify-center overflow-hidden">
        <div 
          className="absolute inset-0 z-0"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1532996122724-e3c354a0b15b?w=1920&q=80)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-br from-neutral-900/80 via-neutral-900/70 to-emerald-900/60"></div>
        </div>
        
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 z-10 text-center py-12 sm:py-16 lg:py-20">
          <div className="inline-flex items-center space-x-2 bg-emerald-500/10 backdrop-blur-sm border border-emerald-500/20 rounded-full px-3 sm:px-4 py-1.5 sm:py-2 mb-4 sm:mb-6 lg:mb-8">
            <Sparkles className="text-emerald-400" size={14} />
            <span className="text-emerald-300 text-xs sm:text-sm font-medium">{t('hero.badge')}</span>
          </div>
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-7xl font-bold text-white mb-4 sm:mb-6 lg:mb-8 leading-tight">
            {t('hero.title')}
            <br />
            <span className="gradient-text bg-gradient-to-r from-emerald-400 to-teal-400 bg-clip-text text-transparent">{t('hero.subtitle')}</span>
          </h1>
          <p className="text-base sm:text-lg md:text-xl lg:text-2xl text-neutral-200 mb-6 sm:mb-8 lg:mb-12 max-w-3xl mx-auto leading-relaxed px-2">
            {t('hero.description')}
          </p>
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center px-4 sm:px-0">
            <Button asChild size="lg" className="bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white text-base sm:text-lg px-6 sm:px-8 py-4 sm:py-6 shadow-2xl shadow-emerald-500/30">
              <Link to="/products">
                {t('hero.viewCatalog')}
                <ArrowRight className="ml-2" size={18} />
              </Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="bg-white/95 backdrop-blur-sm hover:bg-white text-neutral-900 text-base sm:text-lg px-6 sm:px-8 py-4 sm:py-6 border-2 border-white/50">
              <Link to="/contact">{t('hero.requestQuote')}</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Key Features */}
      <section className="py-12 sm:py-16 lg:py-24 bg-gradient-to-b from-white to-neutral-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6 lg:gap-8">
            <div className="text-center group">
              <div className="inline-flex items-center justify-center w-14 h-14 sm:w-16 sm:h-16 lg:w-20 lg:h-20 rounded-xl sm:rounded-2xl bg-gradient-to-br from-emerald-50 to-teal-50 text-emerald-600 mb-3 sm:mb-4 lg:mb-6 group-hover:scale-110 transition-transform shadow-lg shadow-emerald-100">
                <Shield className="w-6 h-6 sm:w-7 sm:h-7 lg:w-9 lg:h-9" />
              </div>
              <h3 className="font-semibold text-sm sm:text-base lg:text-xl mb-1 sm:mb-2 lg:mb-3 text-neutral-800">{t('features.compliant.title')}</h3>
              <p className="text-neutral-600 text-xs sm:text-sm leading-relaxed hidden sm:block">{t('features.compliant.description')}</p>
            </div>
            <div className="text-center group">
              <div className="inline-flex items-center justify-center w-14 h-14 sm:w-16 sm:h-16 lg:w-20 lg:h-20 rounded-xl sm:rounded-2xl bg-gradient-to-br from-emerald-50 to-teal-50 text-emerald-600 mb-3 sm:mb-4 lg:mb-6 group-hover:scale-110 transition-transform shadow-lg shadow-emerald-100">
                <Factory className="w-6 h-6 sm:w-7 sm:h-7 lg:w-9 lg:h-9" />
              </div>
              <h3 className="font-semibold text-sm sm:text-base lg:text-xl mb-1 sm:mb-2 lg:mb-3 text-neutral-800">{t('features.custom.title')}</h3>
              <p className="text-neutral-600 text-xs sm:text-sm leading-relaxed hidden sm:block">{t('features.custom.description')}</p>
            </div>
            <div className="text-center group">
              <div className="inline-flex items-center justify-center w-14 h-14 sm:w-16 sm:h-16 lg:w-20 lg:h-20 rounded-xl sm:rounded-2xl bg-gradient-to-br from-emerald-50 to-teal-50 text-emerald-600 mb-3 sm:mb-4 lg:mb-6 group-hover:scale-110 transition-transform shadow-lg shadow-emerald-100">
                <Truck className="w-6 h-6 sm:w-7 sm:h-7 lg:w-9 lg:h-9" />
              </div>
              <h3 className="font-semibold text-sm sm:text-base lg:text-xl mb-1 sm:mb-2 lg:mb-3 text-neutral-800">{t('features.bulk.title')}</h3>
              <p className="text-neutral-600 text-xs sm:text-sm leading-relaxed hidden sm:block">{t('features.bulk.description')}</p>
            </div>
            <div className="text-center group">
              <div className="inline-flex items-center justify-center w-14 h-14 sm:w-16 sm:h-16 lg:w-20 lg:h-20 rounded-xl sm:rounded-2xl bg-gradient-to-br from-emerald-50 to-teal-50 text-emerald-600 mb-3 sm:mb-4 lg:mb-6 group-hover:scale-110 transition-transform shadow-lg shadow-emerald-100">
                <Users className="w-6 h-6 sm:w-7 sm:h-7 lg:w-9 lg:h-9" />
              </div>
              <h3 className="font-semibold text-sm sm:text-base lg:text-xl mb-1 sm:mb-2 lg:mb-3 text-neutral-800">{t('features.support.title')}</h3>
              <p className="text-neutral-600 text-xs sm:text-sm leading-relaxed hidden sm:block">{t('features.support.description')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Products */}
      <section className="py-12 sm:py-16 lg:py-24 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 sm:mb-12 lg:mb-16">
            <span className="inline-block text-emerald-600 font-semibold text-xs sm:text-sm uppercase tracking-wider mb-2 sm:mb-3">{t('products.subtitle')}</span>
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-neutral-900 mb-3 sm:mb-4 lg:mb-6">{t('products.featured')}</h2>
            <p className="text-neutral-600 text-sm sm:text-base lg:text-lg max-w-2xl mx-auto leading-relaxed px-4 sm:px-0">
              {t('products.featuredDescription')}
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8 mb-8 sm:mb-10 lg:mb-12">
            {translatedFeaturedProducts.map((product) => (
              <Card key={product.id} className="overflow-hidden hover:shadow-2xl transition-all duration-300 border-0 shadow-lg card-hover group">
                <div className="aspect-square bg-neutral-50 overflow-hidden relative">
                  <img 
                    src={product.image} 
                    alt={product.name}
                    className="w-full h-full object-contain group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute top-3 sm:top-4 right-3 sm:right-4">
                    <span className="inline-block text-xs font-bold text-white bg-emerald-600 px-2.5 sm:px-3 py-1 sm:py-1.5 rounded-full shadow-lg">
                      {product.capacity}
                    </span>
                  </div>
                </div>
                <CardContent className="p-4 sm:p-6 lg:p-8">
                  <div className="mb-3 sm:mb-4">
                    <h3 className="text-lg sm:text-xl lg:text-2xl font-semibold mb-2 text-neutral-900">{product.name}</h3>
                    <span className="inline-block text-xs font-medium text-emerald-700 bg-emerald-50 px-2.5 sm:px-3 py-1 rounded-full uppercase">
                      {getTypeTranslation(product.type)}
                    </span>
                  </div>
                  <p className="text-neutral-600 text-xs sm:text-sm mb-4 sm:mb-6 leading-relaxed line-clamp-2">{product.shortDesc}</p>
                  <ul className="space-y-1.5 sm:space-y-2 mb-4 sm:mb-6">
                    <li className="flex items-center text-xs sm:text-sm text-neutral-700">
                      <CheckCircle size={14} className="text-emerald-600 mr-2 sm:mr-3 flex-shrink-0" />
                      <span className="line-clamp-1">{product.material}</span>
                    </li>
                    <li className="flex items-center text-xs sm:text-sm text-neutral-700">
                      <CheckCircle size={14} className="text-emerald-600 mr-2 sm:mr-3 flex-shrink-0" />
                      <span className="line-clamp-1">{product.compliance}</span>
                    </li>
                  </ul>
                  <Button asChild className="w-full bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 shadow-lg shadow-emerald-500/20 text-sm sm:text-base">
                    <Link to={`/products/${product.id}`}>{t('products.viewDetails')}</Link>
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="text-center">
            <Button asChild size="lg" variant="outline" className="border-2 border-emerald-600 text-emerald-600 hover:bg-emerald-50 px-6 sm:px-8 text-sm sm:text-base">
              <Link to="/products">{t('hero.viewCatalog')}</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-12 sm:py-16 lg:py-24 bg-gradient-to-b from-neutral-50 to-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-12 lg:gap-16 items-center">
            <div>
              <span className="inline-block text-emerald-600 font-semibold text-xs sm:text-sm uppercase tracking-wider mb-2 sm:mb-3">{t('whyChoose.title')}</span>
              <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-neutral-900 mb-4 sm:mb-6 lg:mb-8">{t('whyChoose.subtitle')}</h2>
              <div className="space-y-4 sm:space-y-5 lg:space-y-6">
                <div className="flex items-start group">
                  <div className="flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 rounded-lg sm:rounded-xl bg-emerald-100 flex items-center justify-center mt-0.5 sm:mt-1 group-hover:bg-emerald-600 transition-colors">
                    <CheckCircle className="w-5 h-5 sm:w-6 sm:h-6 text-emerald-600 group-hover:text-white transition-colors" />
                  </div>
                  <div className="ml-4 sm:ml-6">
                    <h3 className="font-semibold text-base sm:text-lg lg:text-xl mb-1 sm:mb-2 text-neutral-800">{t('whyChoose.highQuality')}</h3>
                    <p className="text-neutral-600 text-sm sm:text-base leading-relaxed">{t('whyChoose.highQualityDesc')}</p>
                  </div>
                </div>
                <div className="flex items-start group">
                  <div className="flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 rounded-lg sm:rounded-xl bg-emerald-100 flex items-center justify-center mt-0.5 sm:mt-1 group-hover:bg-emerald-600 transition-colors">
                    <CheckCircle className="w-5 h-5 sm:w-6 sm:h-6 text-emerald-600 group-hover:text-white transition-colors" />
                  </div>
                  <div className="ml-4 sm:ml-6">
                    <h3 className="font-semibold text-base sm:text-lg lg:text-xl mb-1 sm:mb-2 text-neutral-800">{t('whyChoose.customization')}</h3>
                    <p className="text-neutral-600 text-sm sm:text-base leading-relaxed">{t('whyChoose.customizationDesc')}</p>
                  </div>
                </div>
                <div className="flex items-start group">
                  <div className="flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 rounded-lg sm:rounded-xl bg-emerald-100 flex items-center justify-center mt-0.5 sm:mt-1 group-hover:bg-emerald-600 transition-colors">
                    <CheckCircle className="w-5 h-5 sm:w-6 sm:h-6 text-emerald-600 group-hover:text-white transition-colors" />
                  </div>
                  <div className="ml-4 sm:ml-6">
                    <h3 className="font-semibold text-base sm:text-lg lg:text-xl mb-1 sm:mb-2 text-neutral-800">{t('whyChoose.weatherResistant')}</h3>
                    <p className="text-neutral-600 text-sm sm:text-base leading-relaxed">{t('whyChoose.weatherResistantDesc')}</p>
                  </div>
                </div>
                <div className="flex items-start group">
                  <div className="flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 rounded-lg sm:rounded-xl bg-emerald-100 flex items-center justify-center mt-0.5 sm:mt-1 group-hover:bg-emerald-600 transition-colors">
                    <CheckCircle className="w-5 h-5 sm:w-6 sm:h-6 text-emerald-600 group-hover:text-white transition-colors" />
                  </div>
                  <div className="ml-4 sm:ml-6">
                    <h3 className="font-semibold text-base sm:text-lg lg:text-xl mb-1 sm:mb-2 text-neutral-800">{t('whyChoose.sustainable')}</h3>
                    <p className="text-neutral-600 text-sm sm:text-base leading-relaxed">{t('whyChoose.sustainableDesc')}</p>
                  </div>
                </div>
              </div>
              <Button asChild size="lg" className="mt-6 sm:mt-8 lg:mt-10 bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 shadow-lg shadow-emerald-500/25 text-sm sm:text-base">
                <Link to="/about">{t('whyChoose.learnMore')}</Link>
              </Button>
            </div>
            <div className="relative order-first lg:order-last">
              <div className="absolute -inset-4 bg-gradient-to-r from-emerald-500/20 to-teal-500/20 rounded-3xl blur-2xl"></div>
              <img 
                src="https://images.unsplash.com/photo-1611284446314-60a58ac0deb9?w=800&q=80" 
                alt="Industrial bins"
                className="relative rounded-xl sm:rounded-2xl shadow-2xl w-full"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 sm:py-16 lg:py-24 bg-gradient-to-br from-emerald-600 via-emerald-500 to-teal-600 text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmZmZmYiIGZpbGwtb3BhY2l0eT0iMC4wNSI+PHBhdGggZD0iTTM2IDE0YzMuMzE0IDAgNi0yLjY4NiA2LTZzLTIuNjg2LTYtNi02LTYgMi42ODYtNiA2IDIuNjg2IDYgNiA2ek0xOCA0MmMzLjMxNCAwIDYtMi42ODYgNi02cy0yLjY4Ni02LTYtNi02IDIuNjg2LTYgNiAyLjY4NiA2IDYgNnoiLz48L2c+PC9nPjwvc3ZnPg==')] opacity-30"></div>
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-4 sm:mb-6">{t('cta.readyToOrder')}</h2>
          <p className="text-base sm:text-lg md:text-xl lg:text-2xl mb-6 sm:mb-8 lg:mb-10 text-emerald-50 max-w-2xl mx-auto leading-relaxed px-4 sm:px-0">
            {t('cta.description')}
          </p>
          <Button asChild size="lg" variant="outline" className="bg-white text-emerald-600 hover:bg-neutral-50 border-0 text-base sm:text-lg px-6 sm:px-10 py-4 sm:py-6 shadow-2xl">
            <Link to="/contact">{t('hero.requestQuote')}</Link>
          </Button>
        </div>
      </section>
    </div>
  );
};

export default Home;
